<template>
  <div class="slider-captcha" :class="{ 'is-verified': verified, 'is-error': !!error }" :aria-busy="loading || checking">
    <div class="captcha-heading"><span>{{ $t('安全验证') }}</span><button type="button" class="captcha-refresh" :disabled="disabled || loading || checking" :aria-label="$t('刷新验证')" @click="reset"><i class="el-icon-refresh" aria-hidden="true" />{{ $t('刷新') }}</button></div>
    <div v-if="challenge && !verified" class="captcha-puzzle" aria-hidden="true">
      <img class="captcha-background" :src="challenge.backgroundImage" alt="" draggable="false">
      <img class="captcha-piece" :src="challenge.pieceImage" alt="" draggable="false" :style="pieceStyle">
    </div>
    <div v-if="verified" class="captcha-verified" role="status" aria-live="polite"><i class="el-icon-success" aria-hidden="true" />{{ $t('验证通过') }}</div>
    <div v-else ref="track" class="captcha-track">
      <div class="captcha-progress" :style="{ width: handleLeft + 44 + 'px' }" />
      <span class="captcha-status" role="status" aria-live="polite">{{ statusText }}</span>
      <button ref="handle" type="button" role="slider" class="captcha-handle" :style="{ transform: `translateX(${handleLeft}px)` }" :disabled="!canDrag" :aria-label="$t('拖动滑块，将拼图移至缺口')" aria-valuemin="0" aria-valuemax="264" :aria-valuenow="Math.round(position)" :aria-valuetext="verified ? $t('验证通过') : `${Math.round(position / 264 * 100)}%`" @pointerdown="startDrag" @pointermove="moveDrag" @pointerup="endDrag" @pointercancel="cancelDrag" @lostpointercapture="cancelDrag" @keydown="handleKeydown">
        <i :class="checking || loading ? 'el-icon-loading' : verified ? 'el-icon-check' : 'el-icon-d-arrow-right'" aria-hidden="true" />
      </button>
    </div>
    <p v-if="!verified" class="captcha-hint">{{ error || $t('拖动滑块，将拼图移至缺口') }}</p>
  </div>
</template>

<script>
import { getCaptcha, verifyCaptcha } from '../../api/system'

export default {
  name: 'SliderCaptcha',
  props: { disabled: { type: Boolean, default: false } },
  data () {
    return { challenge: null, loading: false, checking: false, verified: false, error: '', position: 0, trackWidth: 0, dragging: false, pointerId: null, startX: 0, startPosition: 0, startedAt: 0, revision: 0 }
  },
  computed: {
    canDrag () { return !!(this.challenge && !this.disabled && !this.loading && !this.checking && !this.verified && !this.error) },
    handleLeft () { return this.position / 264 * Math.max(0, this.trackWidth - 46) },
    pieceStyle () { return { width: '17.5%', left: this.position / 320 * 100 + '%', top: this.challenge.pieceY / 160 * 100 + '%' } },
    statusText () {
      if (this.loading) return this.$t('正在加载验证...')
      if (this.checking) return this.$t('正在验证...')
      if (this.verified) return this.$t('验证通过')
      if (this.error) return this.$t('请刷新后重试')
      return this.$t('向右拖动滑块')
    }
  },
  mounted () {
    this.measure()
    window.addEventListener('resize', this.measure)
    this.reset()
  },
  beforeDestroy () {
    this.revision++
    clearTimeout(this.expiryTimer)
    clearTimeout(this.retryTimer)
    window.removeEventListener('resize', this.measure)
  },
  methods: {
    measure () { if (this.$refs.track) this.trackWidth = this.$refs.track.getBoundingClientRect().width },
    clearTimers () { clearTimeout(this.expiryTimer); clearTimeout(this.retryTimer) },
    async reset () {
      if (this.disabled || this.loading || this.checking) return
      const revision = ++this.revision
      this.clearTimers()
      this.dragging = false
      this.startedAt = 0
      this.verified = false
      this.position = 0
      this.challenge = null
      this.error = ''
      this.$emit('change', null)
      this.loading = true
      this.$emit('busy', true)
      try {
        const data = await getCaptcha()
        if (revision !== this.revision) return
        if (!data || data.type !== 'SLIDER' || !/^[a-f0-9]{32}$/.test(data.traceId) || !data.backgroundImage || !data.pieceImage || data.width !== 320 || data.height !== 160 || data.pieceWidth !== 56 || data.pieceHeight !== 56 || !Number.isFinite(data.pieceY) || data.pieceY < 0 || data.pieceY > 104 || !(data.expiresIn > 0)) throw new Error('Invalid slider challenge')
        this.challenge = data
        this.$nextTick(this.measure)
        this.armExpiry(Math.min(data.expiresIn, 120))
      } catch (error) {
        if (revision === this.revision) this.error = this.$t('验证加载失败，点击刷新重试')
      } finally {
        if (revision === this.revision) { this.loading = false; this.$emit('busy', false) }
      }
    },
    armExpiry (seconds) {
      clearTimeout(this.expiryTimer)
      this.expiryTimer = setTimeout(() => {
        this.revision++
        this.verified = false
        this.dragging = false
        this.checking = false
        this.position = 0
        this.error = this.$t('验证已过期，请刷新重试')
        this.$emit('change', null)
        this.$emit('busy', false)
      }, seconds * 1000)
    },
    startDrag (event) {
      if (!this.canDrag || (event.pointerType === 'mouse' && event.button !== 0)) return
      event.preventDefault()
      this.measure()
      this.dragging = true
      this.pointerId = event.pointerId
      this.startX = event.clientX
      this.startPosition = this.position
      this.startedAt = performance.now()
      this.$refs.handle.setPointerCapture(event.pointerId)
    },
    moveDrag (event) {
      if (!this.dragging || event.pointerId !== this.pointerId || !this.canDrag) return
      this.position = Math.max(0, Math.min(264, this.startPosition + (event.clientX - this.startX) / Math.max(1, this.trackWidth - 46) * 264))
    },
    endDrag (event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return
      this.moveDrag(event)
      this.dragging = false
      if (this.$refs.handle.hasPointerCapture(event.pointerId)) this.$refs.handle.releasePointerCapture(event.pointerId)
      this.verify()
    },
    cancelDrag () { if (this.dragging) { this.dragging = false; this.position = 0; this.startedAt = 0 } },
    handleKeydown (event) {
      if (!this.canDrag || !['ArrowRight', 'ArrowLeft', 'Home', 'End', 'Enter', ' '].includes(event.key)) return
      event.preventDefault()
      if (!this.startedAt) this.startedAt = performance.now()
      this.measure()
      if (event.key === 'Enter' || event.key === ' ') return this.verify()
      if (event.key === 'Home') this.position = 0
      else if (event.key === 'End') this.position = 264
      else this.position = Math.max(0, Math.min(264, this.position + (event.key === 'ArrowRight' ? 1 : -1) * (event.shiftKey ? 10 : 2)))
    },
    async verify () {
      if (!this.canDrag) return
      const revision = this.revision
      this.checking = true
      this.$emit('busy', true)
      try {
        const result = await verifyCaptcha({ traceId: this.challenge.traceId, offset: this.position, elapsed: Math.round(performance.now() - this.startedAt) })
        if (revision !== this.revision) return
        if (!result || result.traceId !== this.challenge.traceId || !/^[a-f0-9]{64}$/.test(result.code) || !(result.expiresIn > 0)) throw new Error('Invalid slider proof')
        this.verified = true
        this.armExpiry(Math.min(result.expiresIn, 120))
        this.$emit('change', { traceId: result.traceId, code: result.code })
      } catch (error) {
        if (revision === this.revision) {
          this.position = 0
          this.error = this.$t('验证未通过，请重试')
          this.$emit('change', null)
          this.retryTimer = setTimeout(() => this.reset(), 900)
        }
      } finally {
        if (revision === this.revision) { this.checking = false; this.$emit('busy', false) }
      }
    },
    focusHandle () { if (this.$refs.handle) this.$refs.handle.focus() }
  }
}
</script>

<style lang="scss" scoped>
.slider-captcha { width: 100%; line-height: 1.5; }
.captcha-heading { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; color: #1f2d3d; font-size: 13px; }
.captcha-refresh { display: inline-flex; align-items: center; gap: 4px; border: 0; background: transparent; padding: 3px 0 3px 8px; color: #5f6368; font-size: 12px; cursor: pointer; }
.captcha-refresh:hover { color: #1a73e8; }
.captcha-refresh:disabled { opacity: .45; cursor: default; }
.captcha-puzzle { position: relative; overflow: hidden; width: 100%; padding-top: 50%; border-radius: 6px; background: #eef1f4; margin-bottom: 10px; user-select: none; }
.captcha-background { position: absolute; inset: 0; display: block; width: 100%; height: 100%; }
.captcha-piece { position: absolute; display: block; height: auto; filter: drop-shadow(0 2px 2px rgba(0,0,0,.25)); }
.captcha-verified { display: flex; align-items: center; justify-content: center; gap: 7px; min-height: 42px; border: 1px solid #c5dfcc; border-radius: 6px; background: #edf7ef; color: #188038; font-size: 13px; }
.captcha-verified i { font-size: 16px; }
.captcha-track { position: relative; height: 42px; overflow: hidden; border: 1px solid #dadce0; border-radius: 6px; background: #f6f7f9; user-select: none; }
.captcha-progress { position: absolute; top: 0; left: 0; height: 100%; background: #e8f0fe; }
.captcha-status { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding-left: 35px; color: #80868b; font-size: 12px; pointer-events: none; }
.captcha-handle { position: absolute; left: 1px; top: 1px; width: 44px; height: 38px; padding: 0; border: 1px solid #dadce0; border-radius: 5px; background: #fff; color: #1a73e8; font-size: 18px; cursor: grab; touch-action: none; box-shadow: 0 1px 3px rgba(60,64,67,.12); }
.captcha-handle:active { cursor: grabbing; border-color: #1a73e8; }
.captcha-handle:focus-visible, .captcha-refresh:focus-visible { outline: 2px solid #1a73e8; outline-offset: -3px; }
.captcha-handle:disabled { cursor: default; }
.captcha-hint { margin: 7px 0 0; color: #80868b; font-size: 12px; line-height: 1.6; }
.is-error .captcha-hint { color: #c5221f; }
@media (prefers-reduced-motion: reduce) { * { transition: none; } }
</style>
